import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useDomainManager } from '../../config';
import { getDomainImageAuto, handleImageErrorWithFallback } from '../../utils/imageManager';
import { renderCtaButton } from '../../utils/ctaHelper';
import { usePageTitle } from '../../hooks/usePageTitle';

const GridVariant = ({ heroContent, brand }) => {
  const { brand: themeBrand, domainConfig, cta } = useTheme();
  const mainImageRef = useRef(null);
  const secondaryImageRef = useRef(null);
  const domainManager = useDomainManager(themeBrand?.id);
  
  // Get hostname-specific H1 title
  const h1Title = usePageTitle('home');

  // Reset image elements when domain changes
  useEffect(() => {
    if (mainImageRef.current) {
      Object.keys(mainImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete mainImageRef.current.dataset[key];
        }
      });
    }
    if (secondaryImageRef.current) {
      Object.keys(secondaryImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete secondaryImageRef.current.dataset[key];
        }
      });
    }
  }, [brand.id]);

  // Get domain-specific features content using domain manager
  const getDomainFeatures = () => {
    const features = domainManager.getFeatures();
    if (features) {
      return {
        title: features.title,
        subtitle: features.subtitle,
        description: features.description,
        features: features.items
      };
    }
    
    // Fallback for domains without features configured
    return {
      title: 'Advanced Features',
      subtitle: 'Comprehensive Solutions',
      description: 'Discover the powerful features that make our solutions the best choice for your fleet management needs.',
      features: [
        {
          title: 'Real-time Monitoring',
          description: 'Monitor your fleet in real-time with comprehensive tracking and reporting capabilities.',
          image: getDomainImageAuto(themeBrand.id, 'feature-1', ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']),
          icon: '📊'
        },
        {
          title: 'Advanced Analytics',
          description: 'Get insights with advanced analytics and reporting tools for better decision making.',
          image: getDomainImageAuto(themeBrand.id, 'feature-2', ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']),
          icon: '📈'
        },
        {
          title: 'Easy Integration',
          description: 'Seamless integration with existing systems and third-party applications.',
          image: getDomainImageAuto(themeBrand.id, 'feature-3', ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']),
          icon: '🔗'
        },
        {
          title: '24/7 Support',
          description: 'Round-the-clock support with dedicated customer service and technical assistance.',
          image: getDomainImageAuto(themeBrand.id, 'feature-4', ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']),
          icon: '🆘'
        }
      ]
    };
  };

  // Get domain-specific testimonials content using domain manager
  const getDomainTestimonials = () => {
    const testimonials = domainManager.getTestimonials();
    if (testimonials && testimonials.length > 0) {
      return testimonials;
    }
    
    // Fallback for domains without testimonials configured
    return [
      {
        content: "This grid-based interface is incredibly intuitive and visually appealing. It makes managing our fleet so much easier.",
        author: "Alex Chen",
        role: "Fleet Manager",
        company: "TechCorp",
        avatar: null
      },
      {
        content: "The organized layout helps us quickly access the information we need. Great user experience!",
        author: "Sarah Johnson",
        role: "Operations Director",
        company: "InnovateLab",
        avatar: null
      }
    ];
  };

  const { mainImage, secondaryImage, title, subtitle, features } = heroContent;
  const { title: featureTitle, subtitle: featureSubtitle, description: featureDescription, features: domainFeatures } = getDomainFeatures();
  const domainTestimonials = getDomainTestimonials();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Hero Section - Grid-Based Design */}
      <section className="pt-20 pb-24">
        <div className="max-w-7xl mx-auto px-6">

          {/* Main Grid Layout */}
          <div className="grid grid-cols-12 gap-6">
            {/* Title Section - Large Grid */}
            <div className="col-span-12 lg:col-span-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                  {h1Title || title}
                </h1>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {subtitle}
                </p>
              </div>
            </div>

            {/* CTA Section - Small Grid */}
            <div className="col-span-12 lg:col-span-4">
              <div className="rounded-lg shadow-sm p-8 text-white" style={{backgroundColor: '#195A8E'}}>
                <h3 className="text-xl font-bold mb-4">Get Started</h3>
                <p className="text-blue-100 mb-6">Join thousands of satisfied customers</p>
                <div className="flex gap-3">
                  {renderCtaButton(cta?.primary, 'Request Demo', 'px-6 py-3 bg-white text-blue-600 font-semibold rounded-md hover:bg-gray-100 transition-colors duration-200 text-center whitespace-nowrap', domainConfig?.shopLink)}
                  {renderCtaButton(cta?.secondary, 'Buy Now', 'px-6 py-3 border border-white/30 text-white font-semibold rounded-md hover:bg-white/10 transition-colors duration-200 text-center whitespace-nowrap', domainConfig?.shopLink)}
                </div>
              </div>
            </div>

            {/* Main Image - Large Grid */}
            <div className="col-span-12 lg:col-span-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <img 
                  ref={mainImageRef}
                  src={mainImage} 
                  alt={title}
                  className="w-full h-64 lg:h-80 object-cover"
                  onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-main')}
                />
              </div>
            </div>

            {/* Secondary Image - Small Grid */}
            <div className="col-span-12 lg:col-span-4">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <img 
                  ref={secondaryImageRef}
                  src={secondaryImage} 
                  alt={`${title} - Secondary`}
                  className="w-full h-64 lg:h-80 object-cover"
                  onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-secondary')}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-12 gap-6 mb-12">
            <div className="col-span-12">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">{featureTitle}</h2>
                <p className="text-lg text-gray-600 mb-4">{featureSubtitle}</p>
                {featureDescription && (
                  <p className="text-base text-gray-500 max-w-3xl mx-auto">{featureDescription}</p>
                )}
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-12 gap-6">
            {domainFeatures.map((feature, index) => (
              <div key={index} className="col-span-12 md:col-span-6 lg:col-span-3">
                <div className="rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200 h-full group" style={{backgroundColor: '#195A8E'}}>
                  {/* Feature Image */}
                  <div className="relative h-48 overflow-hidden bg-white">
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => handleImageErrorWithFallback(e, themeBrand.id, `feature-${index + 1}`)}
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                    
                    {/* Icon */}
                    <div className="absolute top-4 right-4 text-2xl bg-white/20 backdrop-blur-sm rounded-full w-10 h-10 flex items-center justify-center">
                      {feature.icon}
                    </div>
                  </div>

                  {/* Feature Content */}
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-white mb-3">{feature.title}</h3>
                    <p className="text-blue-100 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Grid Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {/* Section Title */}
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Recognition & Performance</h2>
            <p className="text-lg text-gray-600">Trusted by industry leaders and recognized for excellence</p>
          </div>

          {/* Award Image - Above stats */}
          <div className="grid mb-12">
             <img
               src={getDomainImageAuto(themeBrand.id, 'awards', ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'])}
               alt="Award Recognition"
               className="w-full h-auto object-contain rounded-lg"
               onError={(e) => handleImageErrorWithFallback(e, themeBrand.id, 'awards')}
             />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-12 gap-6">
            {[
              { number: '50,000+', label: 'Global Clients', color: 'blue' },
              { number: '4.7 million', label: 'Vehicle Subscriptions', color: 'green' },
              { number: '25+', label: 'Years of Innovation', color: 'orange' },
              { number: '24/7', label: 'Support', color: 'purple' },
            ].map((stat, index) => (
              <div key={index} className="col-span-12 md:col-span-6 lg:col-span-3">
                <div className="bg-gray-50 rounded-lg p-6 text-center border border-gray-200">
                  <div className={`text-3xl font-bold text-${stat.color}-600 mb-2`}>{stat.number}</div>
                  <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Grid Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          {/* Section Title */}
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What Our Users Say</h2>
            <p className="text-lg text-gray-600">Trusted by professionals worldwide</p>
          </div>

          {/* Testimonials Grid */}
          <div className="grid grid-cols-12 gap-6">
            {domainTestimonials.map((testimonial, index) => (
              <div key={index} className="col-span-12 md:col-span-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 h-full">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                      {testimonial.avatar ? (
                        <img 
                          src={testimonial.avatar} 
                          alt={testimonial.author}
                          className="w-12 h-12 rounded-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className={`w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center ${testimonial.avatar ? 'hidden' : ''}`}>
                        <span className="text-white font-bold text-lg">
                          {testimonial.author.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-gray-900">
                          {testimonial.author}
                        </p>
                        <p className="text-sm text-gray-500">
                          {testimonial.role}, {testimonial.company}
                        </p>
                      </div>
                    </div>
                    <div className="text-blue-500">
                      <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14.017 21v-7.391C14.017 10.257 16.274 8 19.625 8 20.937 8 22 9.063 22 10.375v1.25C22 12.937 20.937 14 19.625 14h-1.25v7h-4.358z" />
                        <path d="M3.5 21v-7.391C3.5 10.257 5.757 8 9.108 8 10.42 8 11.483 9.063 11.483 10.375v1.25C11.483 12.937 10.42 14 9.108 14h-1.25v7H3.5z" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-gray-600 leading-relaxed mb-6">{testimonial.content}</p>
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GridVariant;
